import { EnumModuloDTONecessitaFirma } from '@framework/api-client/api/inquiries/schemas/enumModuloDTONecessitaFirma.ts';
import {
  getGetFrontRichiesteIdAllegatiQueryKey,
  getGetFrontRichiesteIdModuliIdQueryKey,
  getGetFrontRichiesteIdModuliQueryKey,
  usePostFrontRichiesteIdModuloIdDocumentoFirma,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getGetFrontRichiesteIdRiepilogoDownloadQueryKey } from '@framework/api-client/api/inquiries-compile/riepilogo-richiesta/riepilogo-richiesta.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { type IFrontOfficeModule } from '@framework/common-web/frontoffice/module/IFrontOfficeModule.ts';
import { ModuleStateProgressBar } from '@framework/frontoffice-submission/module/form/title/ModuleStateProgressBar.tsx';
import { DownloadSubmissionModuleButton } from '@framework/frontoffice-submission/submission/download-pdf/DownloadSubmissionModuleButton.tsx';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { SignerPanel } from '@framework/frontoffice-submission/submission/signer/SignerPanel.tsx';
import { message, Typography } from 'antd';
import { type RcFile } from 'antd/es/upload';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { queryClient } from '../../../../core/network/queryClient.ts';
import { getFileItem } from './UploadSignedModuleInput.tsx';

const { Text } = Typography;

export interface ISubmissionModuleSignerProps {
  submission?: RichiestaDTO | null;
  submissionModule?: IFrontOfficeModule | null;
}

export function SubmissionModuleSigner(props: ISubmissionModuleSignerProps) {
  const { submission, submissionModule } = props;
  const { t } = useTranslation();

  const submissionId = submission?.id;
  const submissionModuleId = submissionModule?.moduloRichiestaId;
  const disabled =
    SubmissionLogic.isSubmissionNew(submission) ||
    !submissionModule?.statoValidazione?.visitato;

  const [file, setFile] = useState<RcFile | null>(null);
  const [signedFileItem, setSignedFileItem] = useState(
    submissionModule?.idFileFirmato
      ? getFileItem(submissionModule.idFileFirmato)
      : null,
  );

  const upload = usePostFrontRichiesteIdModuloIdDocumentoFirma();

  const handleClick = useCallback(async () => {
    if (!file) {
      console.error('[Upload] Error: file not found');
      return;
    }

    if (!submissionId || !submissionModuleId) {
      return;
    }

    // NB Per qualche motivo la postFrontRichiesteIdModuloIdDocumentoFirma non converte l'input in formData
    const data = new FormData();
    data.append('file', file);

    try {
      const submissionModule = await upload.mutateAsync({
        idRichiesta: submissionId,
        idRichiestaModulo: submissionModuleId,
        data: data as any,
      });

      const fileId = submissionModule.idFileFirmato;
      if (!fileId) {
        console.error('[Upload] Error: file not found in response', {
          submissionModule,
        });
        message.error(
          t(
            'richieste.firma.messaggio-errore-caricamento-file',
            'Errore durante il caricamento del file, riprovare',
          ),
        );
        return;
      }

      setSignedFileItem(getFileItem(fileId));
      setFile(null);

      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdModuliIdQueryKey(
          submissionId,
          submissionModuleId,
        ),
      );

      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdModuliQueryKey(submissionId),
      );

      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdRiepilogoDownloadQueryKey(submissionId),
      );
      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdAllegatiQueryKey(submissionId),
      );

      message.success(
        t(
          'richieste.firma.messaggio-successo-caricamento-file',
          'Il modulo firmato è stato caricato correttamente.',
        ),
      );
    } catch (err) {
      console.error('[Upload] Error after API', err);
      // biome-ignore format: <tbd>
      message.error(getNetworkErrorMessage(err, t('richieste.firma.messaggio-errore-caricamento-file','Errore durante il caricamento del file, riprovare')));
    }
  }, [file, submissionId, submissionModuleId, t, upload]);

  if (SubmissionLogic.isSubmissionSent(submission)) {
    return null;
  }

  return (
    <SignerPanel
      title={t(
        'richieste.firma.procedura-firma-modulo',
        'Procedura di Firma Modulo',
      )}
      extra={
        submissionModule?.stato ? (
          <ModuleStateProgressBar
            currentState={submissionModule.stato}
            isValid={submissionModule.statoValidazione?.valido}
            hasSignatures={
              submissionModule.necessitaFirma !== EnumModuloDTONecessitaFirma.NO
            }
          />
        ) : undefined
      }
      description={
        <Trans
          i18nKey="richieste.firma.scaricare-il-modulo-per-procedere-alla-firma"
          defaults="Scaricare il modulo <title/>, procedere alla firma digitale e ricaricare il modulo firmato."
          components={{
            title: <Text strong>{submissionModule?.titolo}</Text>,
          }}
        />
      }
      downloadButton={
        <DownloadSubmissionModuleButton
          disabled={disabled}
          submissionId={submissionId}
          submissionModuleId={submissionModuleId}
          ignoreMobile
          forceIcon
          // Scarichiamo sempre quello generato
          downloadType="generato"
        />
      }
      uploadName={`upload-signed-module-${submissionModuleId}`}
      file={file}
      onFileSelect={setFile}
      onUpload={handleClick}
      disabled={disabled}
      isUploading={upload.isLoading}
      signedFile={signedFileItem}
    />
  );
}
